<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '@/stores/accountStore';
import { getDoorSettings, patchDoorSettings } from '@/api/doorSettings';

const accountStore = useAccountStore();
const { accountSettings } = storeToRefs(accountStore);

const jazelId = computed(() => {
  const j = accountSettings.value?.juid;
  return typeof j === 'string' && j.trim() ? j.trim() : '';
});

type SettingBlock = {
  id: string;
  label: string;
  draft: string;
  min?: number;
  max?: number;
  step?: number | string;
  hint?: string;
};

const settingBlocks = ref<SettingBlock[]>([
  {
    id: 'missing-trim',
    label: 'Missing trim threshold (%)',
    draft: '',
    min: 1,
    max: 100,
    step: 1,
  },
  {
    id: 'trim-colors-cache-ttl',
    label: 'Trim colors cache TTL (hours)',
    draft: '',
    min: 1,
    max: 720,
    step: 1,
    hint: 'Empty = server default (6 hours). Max 30 days.',
  },
]);

const blockError = ref<Record<string, string | null>>({});
const loading = ref(false);
const saving = ref(false);
const loadError = ref<string | null>(null);
const saveError = ref<string | null>(null);
const snackbar = ref(false);

function applyMissingTrimToBlocks(v: number | null) {
  const block = settingBlocks.value.find((b) => b.id === 'missing-trim');
  if (block) {
    block.draft = v === null || v === undefined ? '' : String(v);
  }
}

function applyTrimColorsCacheTtlToBlocks(seconds: number | null) {
  const block = settingBlocks.value.find((b) => b.id === 'trim-colors-cache-ttl');
  if (block) {
    if (seconds === null || seconds === undefined) {
      block.draft = '';
    } else {
      const h = seconds / 3600;
      block.draft = Number.isInteger(h) ? String(h) : String(Math.round(h * 100) / 100);
    }
  }
}

async function loadAll() {
  const id = jazelId.value;
  if (!id) {
    loadError.value = 'No Jazel id';
    applyMissingTrimToBlocks(null);
    applyTrimColorsCacheTtlToBlocks(null);
    return;
  }
  loadError.value = null;
  saveError.value = null;
  loading.value = true;
  try {
    const d = await getDoorSettings(id);
    applyMissingTrimToBlocks(d.missingTrimThreshold);
    applyTrimColorsCacheTtlToBlocks(d.trimColorsCacheTtlSeconds ?? null);
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    loadError.value = err?.response?.data?.message ?? err?.message ?? 'Load failed';
    applyMissingTrimToBlocks(null);
    applyTrimColorsCacheTtlToBlocks(null);
  } finally {
    loading.value = false;
  }
}

watch(
  jazelId,
  (id) => {
    if (id) loadAll();
    else {
      loadError.value = null;
      applyMissingTrimToBlocks(null);
      applyTrimColorsCacheTtlToBlocks(null);
    }
  },
  { immediate: true },
);

async function saveAll() {
  const id = jazelId.value;
  if (!id) {
    saveError.value = 'No Jazel id';
    return;
  }
  saveError.value = null;
  for (const b of settingBlocks.value) {
    blockError.value[b.id] = null;
  }

  const missingBlock = settingBlocks.value.find((b) => b.id === 'missing-trim');
  const ttlBlock = settingBlocks.value.find((b) => b.id === 'trim-colors-cache-ttl');
  if (!missingBlock || !ttlBlock) return;

  const rawPct = missingBlock.draft.trim();
  let nextPct: number | null;
  if (rawPct === '') {
    nextPct = null;
  } else {
    const n = Number(rawPct);
    if (!Number.isInteger(n) || n < 1 || n > 100) {
      blockError.value['missing-trim'] = '1–100 or empty';
      return;
    }
    nextPct = n;
  }

  const rawTtl = ttlBlock.draft.trim();
  let nextSeconds: number | null;
  if (rawTtl === '') {
    nextSeconds = null;
  } else {
    const hours = Number(rawTtl);
    if (!Number.isFinite(hours) || hours <= 0) {
      blockError.value['trim-colors-cache-ttl'] = 'Positive hours or empty';
      return;
    }
    nextSeconds = Math.round(hours * 3600);
    const maxSec = 30 * 24 * 60 * 60;
    if (nextSeconds < 60 || nextSeconds > maxSec) {
      blockError.value['trim-colors-cache-ttl'] = 'Must be between 1 minute and 30 days';
      return;
    }
  }

  saving.value = true;
  try {
    const d = await patchDoorSettings(id, {
      missingTrimThreshold: nextPct,
      trimColorsCacheTtlSeconds: nextSeconds,
    });
    applyMissingTrimToBlocks(d.missingTrimThreshold);
    applyTrimColorsCacheTtlToBlocks(d.trimColorsCacheTtlSeconds ?? null);
    snackbar.value = true;
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    saveError.value = err?.response?.data?.message ?? err?.message ?? 'Save failed';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <v-row>
    <v-col v-if="!jazelId" cols="12">
      <p class="text-caption text-medium-emphasis">No account id</p>
    </v-col>
    <v-col v-else-if="loadError" cols="12">
      <p class="text-caption text-error">{{ loadError }}</p>
    </v-col>
  </v-row>

  <v-row>
    <v-col v-for="block in settingBlocks" :key="block.id" cols="12" md="6">
      <v-text-field
        v-model="block.draft"
        :label="block.label"
        type="number"
        :min="block.min"
        :max="block.max"
        :step="block.step ?? 1"
        :hint="block.hint"
        :persistent-hint="Boolean(block.hint)"
        hide-details="auto"
        :disabled="loading || saving || !jazelId"
        :loading="loading"
      />
    </v-col>
  </v-row>
  <v-row>
    <v-col v-for="block in settingBlocks" :key="`${block.id}-err`" cols="12" md="6">
      <p v-if="blockError[block.id]" class="text-caption text-error">{{ blockError[block.id] }}</p>
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="12" class="text-end">
      <v-btn color="primary" :loading="saving" :disabled="loading || !jazelId" @click="saveAll">
        Save
      </v-btn>
    </v-col>
  </v-row>
  <v-row v-if="saveError">
    <v-col cols="12">
      <p class="text-caption text-error">{{ saveError }}</p>
    </v-col>
  </v-row>

  <v-snackbar v-model="snackbar" :timeout="2000" location="bottom">Saved</v-snackbar>
</template>
